export class ODataFilterService {
    static createQueryFilterParams(loadOptions: any): string {
        const params = new URLSearchParams();

        if (loadOptions.filter) {
            const filter = this.devextremeFilterToOData(loadOptions.filter);
            if (filter) {
                params.set('$filter', filter);
            }
        }

        if (loadOptions.sort && loadOptions.sort.length > 0) {
            const orderby = loadOptions.sort
                .map(({ selector, desc }: { selector: string; desc: boolean }) => selector + (desc ? ' desc' : ''))
                .join(',');
            params.set('$orderby', orderby);
        }

        if (typeof loadOptions.skip === 'number') {
            params.set('$skip', loadOptions.skip.toString());
        }

        if (typeof loadOptions.take === 'number') {
            params.set('$top', loadOptions.take.toString());
        }

        if (loadOptions.select && loadOptions.select.length > 0) {
            params.set('$select', loadOptions.select.join(','));
        }

        if (loadOptions.requireTotalCount) {
            params.set('$count', 'true');
        }

        return params.toString();
    }

    private static devextremeFilterToOData(filter: any): string {
        if (!filter) return '';

        if (Array.isArray(filter)) {
            if (filter.length === 2 && filter[0] === '!') {
                const inner = this.devextremeFilterToOData(filter[1]);
                return inner ? `not (${inner})` : '';
            }

            if (typeof filter[0] === 'string' && (filter.length === 3 || filter.length === 2)) {
                const [field, op, value] = filter;
                return this.simpleFilterToOData(field, op, value);
            }

            let i = 1;
            while (i < filter.length) {
                if (typeof filter[i] === 'string' && (filter[i] === 'and' || filter[i] === 'or')) {
                    i += 2;
                } else {
                    break;
                }
            }
            if (i === filter.length) {
                const operator = filter[1];
                const expressions = [];
                for (let j = 0; j < filter.length; j += 2) {
                    const expr = this.devextremeFilterToOData(filter[j]);
                    if (expr) expressions.push(expr);
                }

                if (expressions.length === 0) return '';
                if (expressions.length === 1) return expressions[0];

                return `(${expressions.join(` ${operator} `)})`;
            }

            if (filter.length === 3 && (filter[1] === 'and' || filter[1] === 'or')) {
                const left = this.devextremeFilterToOData(filter[0]);
                const op = filter[1];
                const right = this.devextremeFilterToOData(filter[2]);

                if (!left && !right) return '';
                if (!left) return right;
                if (!right) return left;

                return `(${left} ${op} ${right})`;
            }
        }

        return '';
    }

    private static simpleFilterToOData(field: string, op: string, value: any): string {
        switch (op) {
            case '=':
                return `${field} eq ${this.formatODataValue(value)}`;
            case '<>':
                return `${field} ne ${this.formatODataValue(value)}`;
            case '>':
                return `${field} gt ${this.formatODataValue(value)}`;
            case '>=':
                return `${field} ge ${this.formatODataValue(value)}`;
            case '<':
                return `${field} lt ${this.formatODataValue(value)}`;
            case '<=':
                return `${field} le ${this.formatODataValue(value)}`;
            case 'contains':
                return `contains(${field}, '${value}')`;
            case 'startswith':
                return `startswith(${field}, '${value}')`;
            case 'endswith':
                return `endswith(${field}, '${value}')`;
            default:
                return '';
        }
    }

    private static formatODataValue(value: any): string {
        if (typeof value === 'string') {
            if (this.isDateString(value)) {
                return value;
            }
            return `'${value.replace(/'/g, "''")}'`; // Wrap normal strings
        }

        if (value instanceof Date) {
            const year = value.getFullYear();
            const month = String(value.getMonth() + 1).padStart(2, '0');
            const day = String(value.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`; // Return unquoted ISO date
        }

        if (typeof value === 'boolean') {
            return value ? 'true' : 'false';
        }

        return value;
    }

    private static isDateString(value: any): boolean {
        return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[\+\-]\d{2}:\d{2})?)?$/.test(value);
    }
}
